import { parseToAst } from '@sleepstation/markdown'
import { findById, replaceTokens } from '@sleepstation/utils'
import { pipe, prop, propEq } from 'ramda'
import areYouGettingEnoughSleepHeaderImageLargeUrl from '../../../static/images/are-you-getting-enough-sleep-illustration-large.svg'
import areYouGettingEnoughSleepHeaderImageUrl from '../../../static/images/are-you-getting-enough-sleep-illustration-mobile.svg'
import whatDoYouKnowAboutSleepHeaderImageLargeUrl from '../../../static/images/what-do-you-think-you-know-about-sleep-illustration-large.svg'
import whatDoYouKnowAboutSleepHeaderImageUrl from '../../../static/images/what-do-you-think-you-know-about-sleep-illustration-mobile.svg'
import { DBAS_LEVEL } from '../const/quiz'
import { formatFirstName } from '../helpers/formatting/testimonial'
import { mapObjOfValue } from '../helpers/list'
import privacy from '../md/privacy.txt'
import terms from '../md/terms.txt'
import { getTestimonial, mapTestimonials } from '../utils/relations/testimonial'

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const getDashboardDownloads = pipe(findById(`dashboard`), prop(`downloads`))

const getIs404 = propEq(404, `status`)

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

const CONTENT = {
  template: [
    {
      name: `error`,
      getPageData: ({ error }) => {
        const is404 = getIs404(error)

        return {
          title: is404
            ? `Unfortunately the page you’re looking for does not exist`
            : `There was a problem loading this page`,
          pageUid: `errorPage`,
          featuredImage: `../../../static/images/page-default-image.jpg`,
          metadata: {
            title: is404 ? `404: page not found` : `Problem loading page`,
            shouldIndex: false,
          },
          content: {
            copy: {
              body: parseToAst(
                is404
                  ? `There is no page at **${window.location.pathname}**.`
                  : `Our engineers have been informed and will address the problem.`
              ),
            },
          },
        }
      },
    },
    {
      route: `/portal/the-information-standard`,
      getPageData: () => ({
        title: `The Information Standard`,
        metadata: {
          title: `Sleepstation portal — Are you getting enough sleep?`,
        },
        content: {
          copy: {
            body: parseToAst(`Sleepstation shall hold responsibility for the accuracy of the information they publish and neither the Scheme Operator nor the Scheme Owner shall have any responsibility whatsoever for costs, losses or direct or indirect damages or costs arising from inaccuracy of information or omissions in information published on the website on behalf of Sleepstation. 

[Learn more about the information standard](http://www.england.nhs.uk/tis/t)

[View the information standards member list](http://www.england.nhs.uk/tis/our-members/)`),
          },
        },
      }),
    },
    {
      route: `/portal`,
      getPageData: ({ portal }) => ({
        title: `Sleep well, stay well`,
        metadata: {
          title: `Sleepstation portal for #{fullName}`,
        },
        authenticationConfig: {
          redirectUrlValid: portal.helpers.getPath(`/portal/dashboard/`),
        },
        content: {
          modalError: {
            message: `Something went wrong. Please check your passcode is correct or try again later.`,
          },
          hero: {
            productShot: {
              url: `/images/product-shot-portal-laptop-and-two-phones.png`,
              alt: `The Sleepstation app shown on a laptop and smartphone screen`,
            },
          },
          accessList: {
            title: `Get free access to:`,
            items: mapObjOfValue([
              `Download practical advice from our sleep experts`,
              `Test your understanding of sleep.`,
              `Check if you’re getting enough sleep.`,
              `Access tips to help you sleep better.`,
            ]),
          },
          form: {
            title: `To access the portal, enter the passcode`,
          },
          testimonial: {
            testimonial: getTestimonial(`zahid`),
            toSubtitle: replaceTokens(`from #{location}`),
            toTitle: formatFirstName,
          },
        },
      }),
    },
    {
      route: `/portal/could-you-have-insomnia`,
      getPageData: ({ portal }) => ({
        metadata: {
          title: `Sleepstation portal - Could you have insomnia?`,
          shouldIndex: false,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        content: {
          hero: {
            title: `Could you have insomnia?`,
            image: {
              url: `/images/could-you-have-insomnia-illustration.svg`,
              alt: `Two pillows with eyes on`,
            },
          },
          symptomList: {
            items: mapObjOfValue([
              `Difficulty getting to sleep or staying asleep.`,
              `Distress and/or impairment in quality of life.`,
              `Sleep disturbance three times a week or more`,
            ]),
          },
          testimonial: {
            testimonial: getTestimonial(`jackie`),
            toTitle: formatFirstName,
            toSubtitle: replaceTokens(`#{age}, from #{location}`),
          },
          list: {
            items: mapObjOfValue([
              `Developed by doctors`,
              `Unlimited personal chat`,
              `Clinically proven`,
            ]),
          },
          copy: {
            findOutMoreLink: {
              url: portal.hasActiveLicences
                ? portal.helpers.getPath(`/portal/expert-guidance/`)
                : `#{wwwSite}/articles/sleep-science/online-sleep-clinic/`,
            },
          },
          cta: {
            title: `Take our latest survey`,
            linkLabel: `Start survey`,
            linkUrl: `https://survey.sleepstation.org.uk`,
            body: `You can contribute to our sleep research by completing our latest survey.`,
            trackingId: `portal-could-you-have-insomnia-1`,
          },
          productShot: {
            image: {
              url: `/images/product-shot-phone.png`,
              alt: `Product shot`,
            },
            imageLarge: {
              url: `/images/product-shot-laptop-and-two-phones.png`,
              alt: `Product shot`,
            },
          },
        },
      }),
    },
    {
      route: `/portal/dashboard`,
      getPageData: ({ portal }) => ({
        portal,
        metadata: {
          title: `Sleepstation portal for #{fullName}`,
          shouldIndex: false,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        content: {
          hero: {
            title: `Sleep well, stay well`,
            body: portal.heroDashboard.body,
          },
          knowledgeBasePreview: {
            title: `Knowledge base`,
            body: `Visit our knowledge base to download practical advice to help you sleep better. Each guide provides a summary of the science in an easy to read format. These guides have been thoroughly researched, and include commentary from experts in sleep science.`,
            link: {
              url: portal.helpers.getPath(`/portal/knowledge-base/`),
              label: `Visit the knowledge base`,
            },
            downloads: getDashboardDownloads(portal.downloadsFeatured),
          },
          linkPreview: {
            title: `Learn about your sleep`,
            body: `Many of us hold beliefs about sleep, both our own sleep and the sleep of others. We might think that we need to sleep for a certain number of hours per night or view late risers as lazy. Some of these beliefs can be unhelpful and can actually disrupt sleep. Find out if what you think about sleep might be impacting how well you sleep.`,
            links: [
              {
                title: `What do you know about sleep?`,
                label: `Test your thinking`,
                url: portal.helpers.getPath(
                  `/portal/what-do-you-know-about-sleep/intro/`
                ),
                image: {
                  url: `/images/what-do-you-think-you-know-about-sleep-illustration-mobile-card.svg`,
                  alt: `TBD`,
                },
                body: parseToAst(
                  `Test your beliefs and attitudes about sleep to find out if changing how you think could improve how you feel.`
                ),
              },
              {
                title: `Are you getting enough sleep?`,
                label: `Check your sleep`,
                url: portal.helpers.getPath(
                  `/portal/are-you-getting-enough-sleep/intro/`
                ),
                image: {
                  url: `/images/epworth-illustration-mobile-card.svg`,
                  alt: `TBD`,
                },
                body: parseToAst(
                  `When we’re sleeping well and getting enough sleep, we should feel alert for most of the day. Find out if you’re getting enough sleep.`
                ),
              },
            ],
          },
          cta: {
            title: `Take our latest survey`,
            linkLabel: `Start survey`,
            linkUrl: `https://survey.sleepstation.org.uk`,
            body: `You can contribute to our sleep research by completing our latest survey.`,
            trackingId: `portal-dashboard-1`,
          },
        },
      }),
    },
    {
      route: `/portal/expert-guidance`,
      getPageData: ({ portal }) => ({
        metadata: {
          title: `Sleepstation portal - Expert guidance`,
          shouldIndex: false,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        content: {
          intro: {
            title: `Could you benefit from advice and support from our sleep team?`,
            image: { url: `/images/sleep-diaries-2.png`, alt: `TBD` },
          },
          therapySummary: {
            title: `What’s different about the sleep improvement programme?`,
            image: {
              url: `/images/product-shot-laptop-and-two-phones.png`,
              alt: `TBD`,
            },
          },
          benefitsList: {
            items: mapObjOfValue([
              `fall asleep faster.`,
              `stay asleep longer.`,
              `achieve better quality sleep.`,
            ]),
          },
          videoPlayer: {
            title: `TBD`,
            id: `357734805`,
          },
          cta1: {
            title: `You’re just one click away from the new you.`,
            linkLabel: `Register now`,
            linkUrl: portal.helpers.getLicenceSignupPath(),
            body: `Sleep better. Feel better. Live better.`,
            trackingId: `portal-expert-guidance-2`,
          },
          simpleTestimonials: {
            title: `Why choose Sleepstation?`,
            subtitleTemplate: `#{age}, from #{location}`,
            body: `We’ve helped tens of thousands of people get a good night’s sleep, here’s what some of them have to say about our programme.`,
            items: mapTestimonials([`fay`, `zahid`, `rachel`, `richard`]),
          },
          cta2: {
            title: `Don’t sleep on it. Register today.`,
            linkLabel: `Start now`,
            linkUrl: portal.helpers.getLicenceSignupPath(),
            body: `When we sleep well, we live well.`,
            trackingId: `portal-expert-guidance-2`,
          },
        },
      }),
    },
    {
      route: `/portal/knowledge-base`,
      getPageData: ({ portal }) => ({
        metadata: {
          title: `Sleepstation portal - Knowledge base`,
          shouldIndex: false,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        banners: {
          mutes: [`announcementBanner`],
        },
        content: {
          hero: {
            title: `Knowledge base`,
            body: `While there is still much to be learned about sleep, and that’s what makes the topic so interesting for us, there is also a lot that we do know. Knowledge is powerful. When we know what’s going on with sleep, we are better equipped to improve it. These downloads provide information and practical advice to help you sleep better.`,
          },
          authorsSummary: {
            title: `Prepared by our research team, led by our Director of Sleep Science, Dr Stanley`,
          },
        },
      }),
    },
    {
      route: `/portal/privacy`,
      getPageData: () => ({
        title: `Terms and conditions`,
        featuredImage: `../../../static/images/page-default-image.jpg`,
        metadata: {
          title: `Sleepstation portal — Privacy`,
        },
        content: {
          copy: {
            body: parseToAst(privacy),
          },
        },
      }),
    },
    {
      route: `/portal/are-you-getting-enough-sleep/intro`,
      getPageData: ({ portal }) => ({
        title: `Are you getting enough sleep?`,
        metadata: {
          title: `Sleepstation portal — Are you getting enough sleep?`,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        content: {
          hero: {
            title: `Are you getting enough sleep?`,
            body: `Sleep needs are as individual as height. There are common averages, but everyone is different. When we’re sleeping well and getting enough sleep, we feel alert throughout most of the day. Do you? If not, it’s worth checking if you’re getting enough.`,
            image: {
              url: areYouGettingEnoughSleepHeaderImageUrl,
              urlLarge: areYouGettingEnoughSleepHeaderImageLargeUrl,
              alt: `A hand holding a mobile phone`,
            },
            link: {
              label: `Check your sleep`,
              url: portal.helpers.getPath(
                `/portal/are-you-getting-enough-sleep/questions/`
              ),
            },
          },
        },
      }),
    },
    {
      route: `/portal/are-you-getting-enough-sleep/questions`,
      getPageData: ({ portal }) => ({
        title: `Are you getting enough sleep?`,
        metadata: {
          title: `Sleepstation portal — Are you getting enough sleep?`,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
      }),
    },
    {
      route: `/portal/are-you-getting-enough-sleep/results`,
      getPageData: ({ portal }) => ({
        title: `Are you getting enough sleep?`,
        metadata: {
          title: `Sleepstation portal — Are you getting enough sleep?`,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        content: {
          copy: {
            intro:
              parseToAst(`The questionnaire that you’ve just completed is called the _Epworth Sleepiness Scale (ESS)_. It is a validated measure of daytime sleepiness. When we get enough sleep, we shouldn’t feel sleepy during
          the day.`),
          },
          levelList: {
            items: [
              {
                range: `0–5`,
                label: `lower normal daytime sleepiness`,
                level: `positive`,
              },
              {
                range: `6–10`,
                label: `normal daytime sleepiness`,
                level: `positive`,
              },
              {
                range: `11–12`,
                label: `mild excessive daytime symptoms`,
                level: `neutral`,
              },
              {
                range: `13–15`,
                label: `moderate excessive daytime symptoms`,
                level: `neutral`,
              },
              {
                range: `16–24`,
                label: `severe excessive daytime symptoms`,
                level: `negative`,
              },
            ],
          },
          action: {
            link: {
              hasActiveLicences: {
                title: `Get personalised support and expert guidance`,
                label: `Register today`,
                body: parseToAst(`Did you know that one of the best ways to overcome sleep issues is to tackle them head-on? Don’t bury your head in the sand - or under the pillow - be proactive and take the bull by the horns. **It’s your sleep and it’s your move.**

We can offer you the tools, guidance, experience and support to overcome your sleeping issues and put bad sleep behind. It’s free for you to register as the cost is fully covered by ${portal.fullName}.`),
                image: { url: `/images/product-shot-card.svg`, alt: `TBD` },
                url: portal.helpers.getPath(`/portal/expert-guidance/`),
              },
              default: {
                title: `Could you have insomnia`,
                label: `Find out more`,
                body: parseToAst(
                  `Find out how Sleepstation can help you sleep better.`
                ),
                image: { url: `/images/product-shot-card.svg`, alt: `TBD` },
                url: portal.helpers.getPath(`/portal/could-you-have-insomnia/`),
              },
            },
          },
        },
      }),
    },
    {
      route: `/portal/what-do-you-know-about-sleep/intro`,
      getPageData: ({ portal }) => ({
        title: `What do you know about sleep?`,
        metadata: {
          title: `Sleepstation portal - What do you know about sleep?`,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        content: {
          hero: {
            title: `What do you know about sleep?`,
            body: `What we think and feel about sleep, including how often we think about it, can play a role in determining how well we sleep. It can be helpful to review your beliefs and attitudes to sleep to find out if changing how you _think_ could improve how you _feel_.`,
            image: {
              url: whatDoYouKnowAboutSleepHeaderImageUrl,
              urlLarge: whatDoYouKnowAboutSleepHeaderImageLargeUrl,
              alt: `A hand holding a mobile phone`,
            },
            link: {
              label: `Test your thinking`,
              url: portal.helpers.getPath(
                `/portal/what-do-you-know-about-sleep/questions/`
              ),
            },
          },
        },
      }),
    },
    {
      route: `/portal/what-do-you-know-about-sleep/questions`,
      getPageData: ({ portal }) => ({
        title: `Are you getting enough sleep?`,
        metadata: {
          title: `Sleepstation portal — Are you getting enough sleep?`,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
      }),
    },
    {
      route: `/portal/what-do-you-know-about-sleep/results`,
      getPageData: ({ portal }) => ({
        title: `What do you know about sleep?`,
        metadata: {
          title: `Sleepstation portal - What do you know about sleep?`,
        },
        authenticationConfig: {
          redirectUrlInvalid: portal.helpers.getPath(`/portal/`),
        },
        content: {
          copy: {
            [DBAS_LEVEL.HIGH]: `Your score indicates that you may have unrealistic expectations and thoughts about your sleep. 

This means that **your thoughts and feelings are likely to be negatively affecting your sleep**.

Working on changing how you think could make a big difference to how you feel. Our team can help you with this.`,
            [DBAS_LEVEL.LOW]: `**Low scores are best.** Your score shows that you have reasonable expectations and thoughts about your sleep, which is great. 

**It’s unlikely that your thoughts and feelings about sleep are affecting your sleep**. 

However, if you’re unsatisfied with your sleep then you should aim to find out what’s causing your sleep problem. Our team can help you analyse your sleep further.`,
            [DBAS_LEVEL.LOW_FLAGGED]: `Your score shows that **you have mostly reasonable expectations and thoughts about your sleep**. However, your answers did indicate **strong belief in the following:**

  #{formattedQuestions}

One way of thinking about the reasonableness of our certainties is to see how well they match the facts. Can you think of any examples/evidence would disprove the above?

Unreasonable expectations about sleep can cause and contribute to sleep problems. Working on changing how you think could make a big difference to how you feel. Our team can help you with this.`,
          },
          action: {
            link: {
              title: `Are you getting enough sleep`,
              label: `Check your sleep`,
              body: parseToAst(
                `When we’re sleeping well and getting enough sleep, we should feel alert for most of the day. Find out if you’re getting enough sleep.`
              ),
              image: {
                url: `/images/epworth-illustration-mobile-card.svg`,
                alt: `TBD`,
              },
              url: portal.helpers.getPath(
                `/portal/are-you-getting-enough-sleep/intro`
              ),
            },
          },
        },
      }),
    },
    {
      route: `/portal/terms`,
      getPageData: () => ({
        title: `Terms and conditions`,
        featuredImage: `../../../static/images/page-default-image.jpg`,
        metadata: {
          title: `Sleepstation portal — Terms and conditions`,
        },
        content: {
          copy: {
            body: parseToAst(terms),
          },
        },
      }),
    },
  ],
}

export default CONTENT
